import {Decimal} from 'decimal.js';
import * as _ from 'lodash-es';
import moment from 'moment';
import {CachedNumberFormat} from './cached-number-format';
/**
 * Common numbers related constants and functions
 */
import {dateFormats} from './dates';
import NumberFormat = Intl.NumberFormat;
import NumberFormatOptions = Intl.NumberFormatOptions;

export const DEFAULT_DECIMAL_SEPARATOR = '.';
export const DEFAULT_THOUSAND_SEPARATOR = ',';

export const ZERO_IF_UNDEFINED = true;
export const EMPTY_IF_UNDEFINED = false;
export const TWO_DECIMAL_DIGITS = 2;
export const FOUR_DECIMAL_DIGITS = 4;

export const numberFormat: string = '0,0[.][000]'; // optional decimal digits

export const DEFAULT_FORMAT_LOCALE = 'en-US';

const FORMAT_STYLE_CURRENCY = 'currency';

export function formatPrice(
    value: number | string,
    returnZeroForUndefined: boolean = ZERO_IF_UNDEFINED,
    decimalCount: number = FOUR_DECIMAL_DIGITS,
    currency: string = null
): string {
    if (!returnZeroForUndefined && value === undefined) {
        return '';
    }

    let parsedValue = parseFloat(value as any);

    if (isNaN(parsedValue)) {
        parsedValue = 0;
    }

    const priceFormat = createNumberFormat({isDecimalRequired: true, decimalCount, currency});

    return priceFormat.format(parsedValue);
}

export function formatNumber(
    value: number | string,
    returnZeroForUndefined: boolean = true,
    decimalCount?: number,
    numFormat?: NumberFormat
): string {
    if (!returnZeroForUndefined && (value === undefined || value === null)) {
        return '';
    }

    let parsedValue = parseFloat(value as any);

    if (isNaN(parsedValue)) {
        parsedValue = 0;
    }

    if (!numFormat) {
        numFormat = createNumberFormat({isDecimalRequired: true, decimalCount});
    }

    return numFormat.format(parsedValue);
}

export function commonFormatDateTime(
    value: string | number,
    format: string,
    {toLocal = true}: {toLocal?: boolean} = {}
): string {
    const result = moment.utc(value);

    if (toLocal) {
        result.local(); // Mutable call
    }

    return result.format(format);
}

export function formatDateTime(value: string | number, {toLocal = true}: {toLocal?: boolean} = {}): string {
    return commonFormatDateTime(value, dateFormats.displayDateTimeFormat, {toLocal});
}

export function formatDateTimeSeconds(value: string | number, {toLocal = true}: {toLocal?: boolean} = {}): string {
    return commonFormatDateTime(value, dateFormats.displayDateTimeSecondsFormat, {toLocal});
}

export function formatDate(value: string | number, {toLocal = true}: {toLocal?: boolean} = {}): string {
    return commonFormatDateTime(value, dateFormats.displayDateFormat, {toLocal});
}

export function formatDateWithFormat(
    value: string | number,
    format: string,
    {toLocal = true, locale = ''}: { toLocal?: boolean, locale?: string } = {}
): string {
    const result = moment.utc(value);
    if (toLocal) {
        result.local(); // Mutable call
    }

    if (locale) {
        const localeCode = locale.substr(0, 2);

        result.locale(localeCode);
    }

    return result.format(format);
}

/**
 * Returns css class string for displaying decimal zeroes.
 *
 * @example:
 * 10,000,000.17290
 * "-dec-0-1"
 *
 * 10,000,000.00000
 * "-dec-dot-0-5"
 */
export function classForDisplayDecimalZeros(
    value: number,
    decimalCount: number,
    precision?: number,
    maxDecimalCount?: number
): string {
    let zeroCount: number = 0;
    let actualDecimalCount: number = 0;
    const isValueDefined = !!value || value === 0;

    if (isValueDefined) {
        if (precision) {
            const decimalValue = new Decimal(value).toSignificantDigits(precision);
            actualDecimalCount = decimalValue.decimalPlaces();
        } else if (decimalCount) {
            maxDecimalCount = !!maxDecimalCount || maxDecimalCount === 0
                ? maxDecimalCount
                : decimalCount;

            const decimalValue = new Decimal(value).toDecimalPlaces(maxDecimalCount);
            actualDecimalCount = decimalValue.decimalPlaces();

            zeroCount = Math.max(0, decimalCount - actualDecimalCount);
        }
    }

    return zeroCount
        ? `-dec-${actualDecimalCount ? '' : 'dot-'}0-${zeroCount}`
        : '';
}

export function classForDisplayDecimalZerosFromDecimal(
    decimalValue: Decimal,
    decimalCount: number,
    precision?: number,
    maxDecimalCount?: number
): string {
    let zeroCount: number = 0;
    let actualDecimalCount: number = 0;
    const isValueDefined = !!decimalValue?.isFinite();

    if (isValueDefined) {
        if (precision) {
            decimalValue = decimalValue.toSignificantDigits(precision);
            actualDecimalCount = decimalValue.decimalPlaces();
        } else if (decimalCount) {
            maxDecimalCount = !!maxDecimalCount || maxDecimalCount === 0
                ? maxDecimalCount
                : decimalCount;

            decimalValue = new Decimal(decimalValue).toDecimalPlaces(maxDecimalCount);
            actualDecimalCount = decimalValue.decimalPlaces();

            zeroCount = Math.max(0, decimalCount - actualDecimalCount);
        }
    }

    return zeroCount
        ? `-dec-${actualDecimalCount ? '' : 'dot-'}0-${zeroCount}`
        : '';
}

export function parseClassForDisplayDecimalZeros(displayDecimalClass: string): {
    trailingDecimalZeroCount: number;
    isDecimalPointPresent: boolean;
} {
    let trailingDecimalZeroCount: number = 0;
    let isDecimalPointPresent: boolean = false;

    if (displayDecimalClass && _.startsWith(displayDecimalClass, '-dec-')) {
        if (_.startsWith(displayDecimalClass, '-dec-dot-')) {
            isDecimalPointPresent = true;
        }

        const zeroCountStr = displayDecimalClass.split(' ')[0].split('-0-')[1];

        trailingDecimalZeroCount = Number(zeroCountStr) || 0;
    }

    return {
        trailingDecimalZeroCount,
        isDecimalPointPresent
    };
}

// tslint:disable:cyclomatic-complexity
export function createNumberFormat(
    {
        isDecimalRequired = false,
        decimalCount,
        maxDecimalCount,
        precision,
        currency,
        notation
    }: {
        isDecimalRequired: boolean;
        decimalCount?: number;
        maxDecimalCount?: number;
        precision?: number;
        currency?: string;
        notation?: NumberFormatOptions['notation'];
    }
): NumberFormat {
    const formatOptions: NumberFormatOptions = {};
    const isDecimalCountDefined = !!decimalCount || decimalCount === 0;
    const isPrecisionDefined = !!precision || precision === 0;

    if (isPrecisionDefined) {
        formatOptions.maximumSignificantDigits = precision;

        if (isDecimalRequired) {
            formatOptions.minimumSignificantDigits = precision;
        }
    } else if (isDecimalCountDefined && isDecimalRequired) {
        formatOptions.minimumFractionDigits = decimalCount;
    }

    if (isDecimalCountDefined) {
        const isMaxDecimalCountDefined = !!maxDecimalCount || maxDecimalCount === 0;

        formatOptions.maximumFractionDigits = isMaxDecimalCountDefined
            ? maxDecimalCount
            : decimalCount;
    }

    if (currency) {
        formatOptions.style = FORMAT_STYLE_CURRENCY;
        formatOptions.currency = currency;
    }

    if (notation) {
        formatOptions.notation = notation;
    }

    return CachedNumberFormat.createOrGetFromCache(DEFAULT_FORMAT_LOCALE, formatOptions);
}

/**
 * Limits the passed number to min and max, rounds it to the specified step.
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @param {number} step
 * @param {number} [defaultValue]
 * @returns {number} corrected value.
 */
export function correctNumber(
    value: number,
    min: number,
    max: number,
    step: number,
    defaultValue: number = min
): number {
    value = _.isFinite(value) ? value : defaultValue;

    // Align the value with the step constraint.
    if (_.isFinite(step)) {
        const decimalValue = new Decimal(value);
        const modStepMultiplier = decimalValue.mod(step);
        const minStepMultiplier = decimalValue.sub(modStepMultiplier);

        value = minStepMultiplier.add(
            modStepMultiplier.div(step).round().mul(step)
        ).toNumber();
    }

    return _.clamp(value, min, max);
}
